<!-- Phoenix Table -->
<div class="header-container">
  <div class="table-main-title">{{ header }}</div>
  @if(showResults) {
    <div class="table-results">
      <small><em>{{ dataResultsCount ? dataResultsCount : 0 }} results</em></small>
    </div>
  }
  <!-- Table actions button -->
  @if(config.headerActions) {
  <div class="table-controls">
    @for(action of config.headerActions; track index) {
    @if(action.text) {
    <ods-button variant="tertiary" [disabled]="action.disabled && action.disabled()"
      [attr.aria-label]="action.ariaLabel()" (click)="action.callback($event)">
      {{ action.text }}
    </ods-button>
    }
    }
  </div>
  }
</div>

<div class="table-container" #container (scroll)="checkTableOverflow()" [class.has-overflow]="isOverflowing">
  <!-- Overflow for horizontal scrolling if columns exceed viewport -->
  <div class="table-scroll-indicator" *ngIf="isOverflowing">Scroll to View more columns.</div>
  <!-- itemSize: Specifies the height of the item/one row in table (in pixels). -->
  <cdk-virtual-scroll-viewport part="scrollViewport" [itemSize]="averageRowHeight()" [style.height.px]="config.containerHeight" [minBufferPx]="config.containerHeight" [maxBufferPx]="config.containerHeight">
    <table #table>
      <!-- Table Header -->
      <thead [style.top]="'-' + cdkTranslation">
        <tr>
          <!-- Render the selection column header if selectionMode is enabled -->
          <!-- Checkbox Header -->
          <th style="text-align: center;" *ngIf="config?.selectionMode === 'checkbox'">
            <!--  Sellect all checkbox at header -->
            <input type="checkbox" (change)="onSelectAllToggle($event)">
          </th>
          <!-- Radio Header -->
          <th *ngIf="config?.selectionMode === 'radio'" class="radio-header">{{config?.radioColumnConfig.header}}</th>

          <!-- Column Headers -->
          <ng-container *ngFor="let col of config?.columns">
            <th [ngClass]="{ sortable: col.sortable }"
              [ngStyle]="{'text-align': col.type === 'actions' ? 'center' : 'left' }"
              (click)="col.sortable && toggleSort(col.key)" scope="col" part="{{col.key}}">
              <div [tabindex]="col.sortable ? 0 : -1" (keydown.enter)="col.sortable && toggleSort(col.key)">
                {{ col.title }}
                <!-- Show ascending or descending icon if the column is currently sorted -->
                <img *ngIf="sortColumn === col.key && col.sortable"
                  [src]="sortDirection === 'asc' ? 'assets/icons/column-sort-indicator-ascending.svg' : 'assets/icons/column-sort-indicator-descending.svg'"
                  alt="{{ sortDirection === 'asc' ? 'Ascending' : 'Descending' }}">
                <!-- Show default icon if the column is sortable but not currently sorted -->
                <img *ngIf="col.sortable && sortColumn !== col.key" src="assets/icons/column-sort-indicator.svg"
                  alt="Sortable">
              </div>
            </th>
          </ng-container>
        </tr>
      </thead>

      <!-- Table Body -->
      <tbody>
        <tr *ngIf="_loading; else initial">
          <td [attr.colspan]="getColumnSpan()">
            <app-pol-loader skeletonSize="large" [skeletonRows]="20" skeletonAlignment="table-aligned"
              skeletonType="text" />
          </td>
        </tr>

        <!-- POL Loader Table state messages -->
        <!-- initial message -->
        <ng-template #initial>
          <tr *ngIf="showInitial || isReset; else emptyMessage">
            <td [attr.colspan]="getColumnSpan()">
              <div class="stateMessage">{{ config.initialMessage }}</div>
            </td>
          </tr>
        </ng-template>

        <!-- empty message -->
        <ng-template #emptyMessage>
          <tr *ngIf="showEmptyMessage; else tableDataRows">
            <td [attr.colspan]="getColumnSpan()">
              <div class="stateMessage">{{ config.emptyMessage }}</div>
            </td>
          </tr>
        </ng-template>

        <!-- Table Data Template -->
        <ng-template #tableDataRows>
          <tr #dataRow *cdkVirtualFor="let row of data; trackBy: config.trackBy">
            <!-- Selection Column -->
            <td *ngIf="config?.selectionMode === 'checkbox'">
              <input class="ods-checkbox" type="checkbox" [checked]="selectedRows.has(row)" (change)="onCheckboxChange($event, row)">
            </td>
            <td *ngIf="config?.selectionMode === 'radio'" class="centered">
              <input
                class="ods-radio-button"
                type="radio"
                name="selectRow"
                [checked]="config?.radioColumnConfig?.isChecked(row) || undefined"
                [disabled]="config?.radioColumnConfig?.readOnly || undefined"
                [(ngModel)]="selectedRow"
                [value]="row"
              />
            </td>

            <!-- Data Columns -->
            <ng-container *ngFor="let col of config?.columns; let last = last">
              <td *ngIf="col.restrictedStart && config.isRestricted(row); else unrestricted"
                [attr.colspan]="col.colSpan">
                <span class="restricted-banner">Restricted Access</span>
              </td>
              <ng-template #unrestricted>
                <ng-container *ngIf="!col.hideRestricted || !(config.isRestricted && config.isRestricted(row))">
                  <td *ngIf="col.restrictedIcon && config.isRestricted(row); else normalCell">
                    <app-tooltip icon="locked.svg" position="right">
                      <span>You do not have access to view this member's information.</span>
                      <br>
                      <span>Please contact your OAA if access is required.</span>
                    </app-tooltip>
                    <!-- <img class="lock-icon" src="/assets/images/icons/locked.svg" alt="Restricted"> -->
                    Restricted
                  </td>

                  <ng-template #normalCell>
                    <td *ngIf="!col.type || col.type === 'text'" part="{{ last && 'lastColumn' }}">
                      @if(col.separator) {
                        @for(line of row[col.key].split(col.separator); track $index) {
                          <p class="separated-line">{{ line }}</p>
                        }
                      }
                      @else {
                        {{ row[col.key] }}
                      }
                    </td>

                    <td *ngIf="col.type === 'icon'" class="centered" part="{{ last && 'lastColumn' }}">
                      @if(col.icon(row).isOrchid) {
                        <ods-icon [variant]="col.icon(row).icon" [style]="{ color: col.icon(row).color }" />
                      } @else {
                        <img class="td-icon" [style]="makeIconStyle(col, row)" />
                      }

                      @if(col.icon(row).iconSuffix) {
                        <span class="td-icon-suffix">{{ col.icon(row).iconSuffix }}</span>
                      }
                    </td>

                    <!-- Additional column types like 'date', 'number', etc., can be added here -->
                    <td *ngIf="col.type === 'actions' && config?.hasActions" class="action-items">
                      <!-- Implement action buttons -->
                      <ng-container *ngIf="col.type === 'actions'">
                        <div class="actionRowStyle" *ngIf="!checkArrayType(col?.action); else multiCustomActions">
                          <ods-link *ngIf="col.action?.text; else iconAction"
                            [dataDisabled]="col?.action?.disabled && col?.action?.disabled(row)"
                            [attr.id]="(config?.id ?? 'default') + '-actionBtn' + (i ?? 0)"
                            (click)="col?.action?.callback($event, row)"
                            (keyup.enter)="col?.action?.callback($event, row)"
                            [attr.aria-label]="col?.action?.ariaLabel(row)"
                            [tabindex]="col?.action?.disabled && col?.action?.disabled(row) ? -1 : 0">
                            {{ col.action.text }}
                          </ods-link>
                          <ng-template #iconAction>
                            <ods-icon
                              [variant]="col?.action?.icon"
                              [dataDisabled]="col?.action?.disabled && col?.action?.disabled(row)"
                              [attr.id]="(config?.id ?? 'default') + '-actionBtn' + (i ?? 0)"
                              (click)="col?.action?.callback($event, row)"
                              (keyup.enter)="col?.action?.callback($event, row)"
                              [attr.aria-label]="col?.action?.ariaLabel(row)"
                              [tabindex]="col?.action?.disabled && col?.action?.disabled(row) ? -1 : 0">
                            </ods-icon>
                          </ng-template>
                        </div>
                        <ng-template #multiCustomActions>
                          <div class="actionRowStyle">
                            <ods-link *ngFor="let action of col.action"
                              [dataDisabled]="action.disabled && action.disabled(row)"
                              [attr.id]="(config?.id ?? 'default') + '-actionBtn' + (i ?? 0)"
                              (click)="action.callback($event, row)" (keyup.enter)="action?.callback($event, row)"
                              [attr.aria-label]="action.ariaLabel(row)">
                              {{ action.text }}
                            </ods-link>
                          </div>
                        </ng-template>
                      </ng-container>
                      <ng-container *ngIf="col.actionComputed">
                        <div class="actionRowStyle">
                          <ods-link [dataDisabled]="col.actionComputed(row).disabled(row)"
                            [attr.id]="(config?.id ?? 'default') + '-actionBtn' + (i ?? 0)"
                            (click)="col.actionComputed(row).callback($event, row)"
                            (keyup.enter)="col.actionComputed(row).callback($event, row)"
                            [attr.aria-label]="'Select ' + row.name"
                            [tabindex]="col.actionComputed(row).disabled(row) ? -1 : 0">
                            {{ col.actionComputed(row).text }}
                          </ods-link>
                        </div>
                      </ng-container>
                      <!-- overflow actions -->
                      <ng-container *ngIf="col.overflowActions?.length > 0">
                        <app-overflow-menu icon="/assets/images/icons/more.svg" [config]="col.overflowActions"
                          [metadata]="row" [ariaLabel]="col.overflowActionsAriaLabel(row)"></app-overflow-menu>
                      </ng-container>
                      <ng-container *ngIf="col.overflowActionsComputed">
                        <app-overflow-menu icon="/assets/images/icons/more.svg"
                          [config]="col.overflowActionsComputed(row)" [metadata]="row"
                          [ariaLabel]="col.overflowActionsAriaLabel(row)"></app-overflow-menu>
                      </ng-container>
                    </td>
                    <td *ngIf="col.type === 'date-picker'">
                      <app-phoenix-table-date-input
                        (dateError)="handleDateError($event)"
                        (dateChanged)="handleDateChange($event, row)"
                        [maxDateString_]="col.datePickerOptions?.maxDate"
                      />
                    </td>
                    <td *ngIf="col.type === 'number'">
                      <app-phoenix-table-dollar-amount-input
                        [id_]="row?.id"
                        (dollarAmountChanged)="emitDollarAmountChange($event, row)"
                      />
                    </td>
                    @if(col.type === 'link') {
                      <td>
                        <ods-link (click)="col.linkHandler && col.linkHandler(row)">{{ row[col.key] }}</ods-link>
                      </td>
                    }
                  </ng-template>
                </ng-container>
              </ng-template>
            </ng-container>
            <div *ngIf="row[config.infoMessageProperty]" class="info-message">
              <img src="/assets/images/icons/info.svg" alt="info">
              <small class="info-message-text">
                <strong>{{row[config.infoMessageProperty]}}</strong>
              </small>
            </div>
          </tr>
        </ng-template>
      </tbody>
    </table>
  </cdk-virtual-scroll-viewport>
</div>

<!-- End -->
